import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Eye, EyeOff, Lock, Mail, LayoutDashboard, BarChart3, ShieldCheck } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { handheldLoginReturn } from '@/components/admin/coaching/handheld/handheldNavigation'
import { canAccessAdmin } from '@/utils/roleUtils'
import { resolvePostLoginPath } from '@/utils/authRedirect'
import { getStoredDeviceToken } from '@/api/device'
import { Button3D } from '@/components/learner/ui/Button3D'
import { AdminAccountRecoverySelfServiceModal } from '@/components/auth/AdminAccountRecoverySelfServiceModal'

export default function AdminLoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { login, isLoading, error, clearError } = useAuthStore()
  const { t } = useTranslation('learner')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [recoveryOpen, setRecoveryOpen] = useState(false)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    clearError()

    const formData = new FormData(e.currentTarget)
    const emailValue = (formData.get('email') as string) || email
    const passwordValue = (formData.get('password') as string) || password

    try {
      await login({
        email: emailValue,
        password: passwordValue,
        deviceFingerprint: getStoredDeviceToken(),
      })
      const user = useAuthStore.getState().user
      const returnTo = handheldLoginReturn(searchParams.get('returnTo'))
      navigate(returnTo && (user?.canAccessAdmin || canAccessAdmin(user?.role)) ? returnTo : resolvePostLoginPath(user?.role, 'admin'))
    } catch {
      // 에러는 store 에서 처리
    }
  }

  const formPanel = (
    <div className="max-w-[440px] w-full space-y-8">
      <div className="text-center">
        <h2 className="text-lc-headline-1 text-lc-gray-900">
          {t('auth.adminLogin.title')}
        </h2>
        <p className="mt-2 text-lc-body-3 text-lc-gray-600">
          {t('auth.adminLogin.subtitle')}
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        {error && (
          <div
            role="alert"
            className="flex items-start gap-3 p-4 rounded-lc-md border border-lc-red-900 bg-lc-red-100"
          >
            <AlertCircle className="size-5 text-lc-red-900 mt-0.5 shrink-0" aria-hidden />
            <span className="text-lc-body-3 font-medium text-lc-red-dark">{error}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">
              {t('auth.adminLogin.emailLabel')}
            </label>
            <div className="flex items-center gap-3 h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white transition focus-within:border-[1.5px] focus-within:border-lc-lightblue-900">
              <Mail className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-transparent outline-none text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500"
                placeholder={t('auth.adminLogin.emailPlaceholder')}
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-[14px] font-semibold text-lc-gray-800 mb-1.5">
              {t('auth.adminLogin.passwordLabel')}
            </label>
            <div className="flex items-center gap-3 h-12 px-4 rounded-lc-md border border-lc-gray-200 bg-white transition focus-within:border-[1.5px] focus-within:border-lc-lightblue-900">
              <Lock className="size-5 text-lc-gray-500 shrink-0" aria-hidden />
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="flex-1 bg-transparent outline-none text-lc-body-2 text-lc-gray-700 placeholder-lc-gray-500"
                placeholder={t('auth.adminLogin.passwordPlaceholder')}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="size-8 inline-flex items-center justify-center text-lc-gray-500 hover:text-lc-gray-700 rounded-md"
                aria-label={showPassword ? t('auth.password.hide') : t('auth.password.show')}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="size-[18px]" aria-hidden /> : <Eye className="size-[18px]" aria-hidden />}
              </button>
            </div>
          </div>
        </div>

        <Button3D
          type="submit"
          variant="lightblue"
          size="L"
          disabled={isLoading || !email || !password}
          className="w-full"
        >
          {isLoading ? t('auth.adminLogin.submitting') : t('auth.adminLogin.submit')}
        </Button3D>
      </form>

      <div className="text-center mt-4">
        <button
          type="button"
          onClick={() => setRecoveryOpen(true)}
          className="text-lc-body-3 text-lc-lightblue-dark hover:underline"
        >
          {t('auth.adminRecovery.linkText')}
        </button>
      </div>

      <AdminAccountRecoverySelfServiceModal
        isOpen={recoveryOpen}
        onClose={() => setRecoveryOpen(false)}
      />
    </div>
  )

  return (
    <div className="min-h-screen md:grid md:grid-cols-2 bg-lc-gray-c100">
      <aside
        className="hidden md:flex relative overflow-hidden flex-col justify-between p-10 lg:p-12"
        style={{ background: 'linear-gradient(135deg, #EEF9FF 0%, #E2F8F3 55%, #DDF3FF 100%)' }}
        aria-hidden
      >
        {/* Decorative blurred orbs */}
        <div
          className="pointer-events-none absolute -top-24 -left-24 w-[26rem] h-[26rem] rounded-full opacity-70 blur-3xl"
          style={{ background: 'radial-gradient(circle, #BBE7FF 0%, transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute top-1/3 -right-28 w-80 h-80 rounded-full opacity-60 blur-3xl"
          style={{ background: 'radial-gradient(circle, #C5F1E7 0%, transparent 70%)' }}
        />
        <div
          className="pointer-events-none absolute -bottom-32 left-1/4 w-[28rem] h-[28rem] rounded-full opacity-50 blur-3xl"
          style={{ background: 'radial-gradient(circle, #99DBFF 0%, transparent 70%)' }}
        />

        {/* Subtle dot pattern overlay */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.18]"
          style={{
            backgroundImage: 'radial-gradient(circle, #2E96CE 1px, transparent 1px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="relative">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lc-pill bg-white/70 backdrop-blur text-[13px] font-semibold text-lc-gray-800 shadow-sm">
            {t('auth.adminLogin.heroTagline')}
          </span>
          <h2 className="mt-6 text-lc-display-2 text-lc-gray-900 leading-tight">
            {t('auth.adminLogin.heroTitleLine1')}<br />
            <span className="text-lc-lightblue-dark">{t('auth.adminLogin.heroTitleLine2')}</span>
          </h2>
        </div>

        {/* Abstract admin-dashboard preview cards */}
        <div className="relative flex items-end gap-4">
          <div className="rounded-2xl bg-white/70 backdrop-blur-md border border-white shadow-xl p-4 w-48 -rotate-2">
            <div className="flex items-center gap-2 mb-3">
              <div className="size-9 rounded-xl bg-lc-lightblue-200 flex items-center justify-center">
                <LayoutDashboard className="size-5 text-lc-lightblue-dark" aria-hidden />
              </div>
              <div className="flex-1">
                <div className="h-2 bg-lc-gray-200 rounded-full w-3/4 mb-1" />
                <div className="h-2 bg-lc-gray-100 rounded-full w-1/2" />
              </div>
            </div>
            <div className="h-12 rounded-lg bg-gradient-to-br from-lc-lightblue-100 to-lc-lightblue-200" />
          </div>

          <div className="rounded-2xl bg-white/75 backdrop-blur-md border border-white shadow-xl p-4 w-48 -mb-2">
            <div className="flex items-center justify-between mb-3">
              <div className="size-9 rounded-xl bg-lc-mint-200 flex items-center justify-center">
                <BarChart3 className="size-5 text-lc-mint-dark" aria-hidden />
              </div>
              <ShieldCheck className="size-4 text-lc-mint-dark" aria-hidden />
            </div>
            <div className="flex items-end gap-1.5 h-12">
              <div className="flex-1 rounded-t-md bg-lc-mint-200 h-1/3" />
              <div className="flex-1 rounded-t-md bg-lc-mint-300 h-2/3" />
              <div className="flex-1 rounded-t-md bg-lc-mint-900 h-full" />
              <div className="flex-1 rounded-t-md bg-lc-mint-300 h-1/2" />
              <div className="flex-1 rounded-t-md bg-lc-mint-900 h-3/4" />
            </div>
          </div>
        </div>
      </aside>

      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12">
        {formPanel}
      </div>
    </div>
  )
}
